import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, SearchX, RefreshCw, AlertCircle, ArrowUpRight } from 'lucide-react';
import {
  STATUS_META,
  buildPageList,
  formatInr,
  formatShortDate,
  formatSignedInr,
  formatSignedPercent,
} from '../../utils/gmpBoard';

const Dash = () => <span className="text-slate-300">—</span>;

const gmpTone = (value) => {
  if (value > 0) return { text: 'text-emerald-600', pill: 'bg-emerald-50 text-emerald-700' };
  if (value < 0) return { text: 'text-rose-600', pill: 'bg-rose-50 text-rose-600' };
  return { text: 'text-slate-600', pill: 'bg-slate-100 text-slate-600' };
};

// Statuses worth a badge next to the board tag; others show the exchange instead
const BADGE_STATUSES = ['closing_today', 'upcoming', 'allotted', 'listed'];

const FireRating = ({ rating, label }) => (
  <div className="leading-tight" title={`Source rating: ${rating}/5`}>
    <div className="text-[13px] tracking-[1px]" aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < rating ? 'text-amber-400' : 'text-slate-200'}>
          ★
        </span>
      ))}
    </div>
    <div className="text-[11px] text-slate-500 font-medium whitespace-nowrap">{label}</div>
  </div>
);

const DayGmpNote = ({ value }) =>
  value !== null && value !== undefined ? (
    <div className="text-[10px] text-slate-400 whitespace-nowrap" title="GMP recorded on this day">
      GMP {formatSignedInr(value)}
    </div>
  ) : null;

const HEADERS = [
  { label: 'Company Name', className: 'sticky left-0 z-10 bg-slate-50 min-w-[210px] pl-4 sm:pl-5' },
  { label: 'GMP (₹ & %)' },
  { label: 'Fire Rating' },
  { label: 'Sub (x)', className: 'text-right' },
  { label: 'Price (₹)', className: 'text-right' },
  { label: 'Est. Listing', className: 'text-right bg-emerald-50 text-emerald-700' },
  { label: 'Size (Cr)', className: 'text-right' },
  { label: 'Lot', className: 'text-right' },
  { label: 'Open' },
  { label: 'Close' },
  { label: 'BOA' },
  { label: 'Listing' },
  { label: 'GMP L / H', className: 'text-right' },
  { label: 'Updated', className: 'pr-4 sm:pr-5' },
];

const SkeletonRows = () =>
  Array.from({ length: 6 }).map((_, idx) => (
    <tr key={`skel-${idx}`} className="animate-pulse">
      <td className="sticky left-0 bg-white py-3.5 pl-4 sm:pl-5 pr-3">
        <div className="w-40 h-4 bg-slate-200 rounded" />
        <div className="w-20 h-3 bg-slate-100 rounded mt-2" />
      </td>
      {HEADERS.slice(1).map((h) => (
        <td key={h.label} className="py-3.5 px-3">
          <div className="w-14 h-4 bg-slate-200 rounded" />
        </td>
      ))}
    </tr>
  ));

export const GmpBoardTable = ({
  rows,
  totalRecords,
  filteredCount,
  page,
  pageSize,
  onPageChange,
  isLoading,
  error,
  isRefreshing,
  onRefresh,
  refreshedLabel,
  sourceLabel,
  title = 'Current IPO Grey Market Premium Board',
  noun = 'IPOs',
}) => {
  const navigate = useNavigate();
  const totalPages = Math.max(1, Math.ceil(filteredCount / pageSize));
  const firstIndex = filteredCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastIndex = Math.min(page * pageSize, filteredCount);

  let body;
  if (isLoading) {
    body = <SkeletonRows />;
  } else if (error) {
    body = (
      <tr>
        <td colSpan={HEADERS.length} className="py-14 text-center">
          <div className="max-w-md mx-auto px-4">
            <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
              <AlertCircle className="w-5 h-5" />
            </div>
            <p className="mt-3 font-semibold text-slate-700 text-sm">GMP data is temporarily unavailable</p>
            <p className="text-xs text-slate-400 mt-1">{error}</p>
            <button
              type="button"
              onClick={onRefresh}
              className="mt-3 px-4 py-1.5 bg-[#008080] text-white text-xs font-bold rounded-lg hover:bg-[#006666] transition-colors"
            >
              Retry Loading GMP
            </button>
          </div>
        </td>
      </tr>
    );
  } else if (rows.length === 0) {
    body = (
      <tr>
        <td colSpan={HEADERS.length} className="py-14 text-center">
          <SearchX className="w-8 h-8 mx-auto text-slate-300" />
          <p className="mt-2 font-semibold text-slate-700 text-sm">
            {totalRecords === 0 ? `No ${noun} are being tracked in the grey market right now` : `No ${noun} match this filter`}
          </p>
          {totalRecords > 0 && (
            <p className="text-xs text-slate-500">Pick another filter above to see more issues.</p>
          )}
        </td>
      </tr>
    );
  } else {
    body = rows.map((row) => {
      const upcoming = row.status === 'upcoming';
      const muted = upcoming ? 'text-slate-400' : 'text-slate-700';
      const tone = row.hasGmp ? gmpTone(row.gmp) : null;
      const status = STATUS_META[row.status] || STATUS_META.other;
      // Unquoted issues report 0/0; show a dash rather than a flat ₹0 range
      const hasRange = row.hasGmp && (row.gmpDayLow !== null || row.gmpDayHigh !== null);

      return (
        <tr
          key={row.id}
          onClick={row.slug ? () => navigate(`/ipo-gmp/${row.slug}`) : undefined}
          className={`group hover:bg-slate-50/80 transition-colors ${row.slug ? 'cursor-pointer' : ''}`}
        >
          {/* Company */}
          <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 transition-colors py-3 pl-4 sm:pl-5 pr-3 min-w-[210px]">
            {row.slug ? (
              <Link
                to={`/ipo-gmp/${row.slug}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 font-bold text-slate-900 leading-snug hover:text-[#008080] group-hover:text-[#008080] transition-colors"
              >
                {row.name}
                <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </Link>
            ) : (
              <div className="font-bold text-slate-900 leading-snug">{row.name}</div>
            )}
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <span
                className={`inline-flex items-center px-1.5 py-px rounded text-[10px] font-bold uppercase tracking-wide border ${
                  row.board === 'SME'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                }`}
              >
                {row.board}
              </span>
              {BADGE_STATUSES.includes(row.status) && (
                <span
                  className={`inline-flex items-center px-1.5 py-px rounded text-[10px] font-bold uppercase tracking-wide border ${status.className}`}
                >
                  {status.label}
                </span>
              )}
              {row.exchange && <span className="text-[11px] text-slate-400">{row.exchange}</span>}
            </div>
          </td>

          {/* GMP */}
          <td className="py-3 px-3 whitespace-nowrap">
            {row.hasGmp ? (
              <div className="leading-tight">
                <div className={`font-extrabold text-sm ${tone.text}`}>{formatSignedInr(row.gmp)}</div>
                {row.gmpPercent !== null && (
                  <span className={`inline-block mt-1 px-1.5 py-px rounded text-[11px] font-bold ${tone.pill}`}>
                    {formatSignedPercent(row.gmpPercent)}
                  </span>
                )}
              </div>
            ) : (
              <div className="leading-tight">
                <Dash />
                <div className="text-[11px] text-slate-400">Not quoted yet</div>
              </div>
            )}
          </td>

          {/* Fire Rating */}
          <td className="py-3 px-3">
            <FireRating rating={row.fireRating} label={row.sentiment} />
          </td>

          {/* Subscription */}
          <td className={`py-3 px-3 text-right whitespace-nowrap font-semibold ${muted}`}>
            {row.subscription !== null ? `${row.subscription.toFixed(2)}x` : <Dash />}
          </td>

          {/* Price */}
          <td className="py-3 px-3 text-right whitespace-nowrap text-slate-700">
            {row.price !== null ? formatInr(row.price) : <span className="text-slate-400">TBA</span>}
          </td>

          {/* Est. Listing */}
          <td className="py-3 px-3 text-right whitespace-nowrap bg-emerald-50/60 font-extrabold text-emerald-700">
            {row.estListing !== null ? formatInr(row.estListing) : <Dash />}
          </td>

          {/* Size */}
          <td className="py-3 px-3 text-right whitespace-nowrap text-slate-700">
            {row.sizeCr !== null ? `${formatInr(row.sizeCr, 2)} Cr` : <Dash />}
          </td>

          {/* Lot */}
          <td className="py-3 px-3 text-right whitespace-nowrap text-slate-700">
            {row.lotSize !== null ? row.lotSize.toLocaleString('en-IN') : <Dash />}
          </td>

          {/* Open */}
          <td className={`py-3 px-3 whitespace-nowrap ${muted}`}>
            <div>{formatShortDate(row.openOn)}</div>
            <DayGmpNote value={row.openDayGmp} />
          </td>

          {/* Close */}
          <td className="py-3 px-3 whitespace-nowrap">
            <div className={row.status === 'closing_today' ? 'font-bold text-rose-600' : muted}>
              {formatShortDate(row.closeOn)}
            </div>
            {row.isClosed && <div className="text-[10px] text-rose-500">(Closed)</div>}
            {row.status === 'closing_today' && <div className="text-[10px] text-rose-500">Today</div>}
            <DayGmpNote value={row.closeDayGmp} />
          </td>

          {/* BOA */}
          <td className={`py-3 px-3 whitespace-nowrap ${muted}`}>{formatShortDate(row.boaOn)}</td>

          {/* Listing */}
          <td className={`py-3 px-3 whitespace-nowrap font-semibold ${upcoming ? 'text-slate-400' : 'text-slate-800'}`}>
            {formatShortDate(row.listingOn)}
          </td>

          {/* GMP day range */}
          <td className="py-3 px-3 text-right whitespace-nowrap text-slate-700">
            {hasRange ? (
              <>
                {row.gmpDayLow !== null ? formatInr(row.gmpDayLow) : '—'}
                <span className="text-slate-300"> / </span>
                {row.gmpDayHigh !== null ? formatInr(row.gmpDayHigh) : '—'}
              </>
            ) : (
              <Dash />
            )}
          </td>

          {/* Source updated */}
          <td className="py-3 px-3 pr-4 sm:pr-5 whitespace-nowrap text-[11px] text-slate-500">
            {row.updatedOn || <Dash />}
          </td>
        </tr>
      );
    });
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
      {/* Card Header */}
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 px-4 sm:px-5 py-4 border-b border-slate-200/80">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base sm:text-lg font-extrabold text-slate-900 tracking-tight">{title}</h2>
            {totalRecords > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-teal-50 text-[#008080] border border-teal-200 whitespace-nowrap">
                {totalRecords} Records
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Indicative rates from unofficial market circles; updated as dealer quotes shift.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-[11px] text-slate-400 font-medium whitespace-nowrap">
            BOA = Basis of Allotment&nbsp;&nbsp;|&nbsp;&nbsp;L / H = Day low / high GMP
          </p>
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            title="Refresh GMP data"
            aria-label="Refresh GMP data"
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-[#008080] hover:border-teal-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-slim">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200/80 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              {HEADERS.map((h) => (
                <th key={h.label} className={`py-3 px-3 whitespace-nowrap ${h.className || ''}`}>
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">{body}</tbody>
        </table>
      </div>

      {/* Footer: count, source + pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-slate-200/80 bg-slate-50/70">
        <p className="text-xs text-slate-500 text-center sm:text-left">
          {isLoading ? (
            'Loading live grey market quotes…'
          ) : error ? (
            'Live grey market quotes could not be loaded.'
          ) : (
            <>
              Showing <span className="font-semibold text-slate-700">{firstIndex}</span> to{' '}
              <span className="font-semibold text-slate-700">{lastIndex}</span> of{' '}
              <span className="font-semibold text-slate-700">{filteredCount}</span> {noun.replace(/IPOs$/, 'IPO offerings')}
              {refreshedLabel && <> · Refreshed {refreshedLabel}</>}
              {sourceLabel && <> · Source: {sourceLabel}</>}
            </>
          )}
        </p>

        {totalPages > 1 && !isLoading && !error && (
          <nav className="flex items-center gap-1" aria-label="GMP board pagination">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Previous
            </button>
            {buildPageList(page, totalPages).map((p, i) =>
              p === '…' ? (
                <span key={`gap-${i}`} className="w-7 text-center text-xs text-slate-400">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  aria-current={p === page ? 'page' : undefined}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${
                    p === page
                      ? 'bg-[#008080] text-white'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {p}
                </button>
              )
            )}
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </nav>
        )}
      </div>
    </div>
  );
};

export default GmpBoardTable;
